import { dateFormat, dateTimeFormat, timeFormat } from "ApiConstraints";
import {
  format as dateFnsFormat,
  formatRelative as dateFnsFormatRelative,
  getYear,
  isValid as dateFnsIsValid,
  parse as dateFnsParse,
  parseISO,
} from "date-fns";
import { de, fr, it } from "date-fns/locale";

import { createLogger } from "../logging";

export type DateFieldValue = string | Date | undefined | null;
export const dateFormatWithWeekday = "EE, dd.MM.yyyy";
export const urlDateFormat = "yyyy-MM-dd";
export const minYear = 1950;

const logger = createLogger("DateUtils");

export const tryFormatDate = (value: DateFieldValue, formatStr: string) => {
  const date = parseDateLenient(value);
  if (!date) {
    logger.trace("tryFormatDate(): input is empty -> undefined.");
    return undefined;
  }

  if (!isValidDate(date)) {
    logger.trace("tryFormatDate(): input is not valid -> undefined.");
    return undefined;
  }

  if (!formatStr) {
    logger.trace("tryFormatDate(): format is empty -> undefined.");
    return undefined;
  }

  try {
    return dateFnsFormat(date, formatStr);
  } catch (ex) {
    logger.warn(`tryFormatDate(): ${date} with ${formatStr} returned error`, ex);
    return undefined;
  }
};

export function parseDateOrDefault(
  value: DateFieldValue,
  _format: string,
  defaultValue: Date | undefined
): Date | undefined {
  if (!value) return defaultValue;
  if (value instanceof Date) return value;
  let result: Date | undefined = undefined;

  try {
    result = parseISO(value);
  } catch (ex) {
    logger.trace("parseDateOrDefault(): parseISO failed: ", ex);
  }

  if (!result) {
    try {
      result = parseDateLenient(value);
    } catch (ex) {
      logger.trace("parseDateOrDefault(): parse failed: ", ex);
    }
  }

  if (result && result?.getFullYear() < minYear) {
    logger.trace("parseDateOrDefault(): result is < 1950");
    result = undefined;
  }

  const isValid = isValidDate(result);
  if (!isValid) {
    logger.trace("parseDateOrDefault(): result is invalid -> returning default", defaultValue);
    return defaultValue;
  }

  logger.trace("parseDateOrDefault(): result is valid, returning", result);
  return result;
}

export const formatDate = (date: DateFieldValue, defaultValue = "") => {
  return tryFormatDate(date, dateFormat) || defaultValue;
};

export const formatDateTime = (date: DateFieldValue, defaultValue = "") => {
  return tryFormatDate(date, dateTimeFormat) || defaultValue;
};

export const formatTime = (date: DateFieldValue, defaultValue = "") => {
  return tryFormatDate(date, timeFormat) || defaultValue;
};

export const parseDate = (dateString: string | null, format: string = dateFormat): Date | null => {
  if (!dateString) return null;
  const date = dateFnsParse(dateString, format, new Date());
  return date;
};

/**
 * Attempts to parse a date string using multiple formats, returning a `Date` object if successful or `null` if parsing fails.
 * This function is lenient and tries various common date formats.
 *
 * Supported formats:
 * - `d.M.yyyy` (e.g., '1.9.2024')
 * - `d.M.yy` (e.g., '1.9.24')
 * - `ddMMyyyy` (e.g., '01092024')
 * - 'dmyy' (e.g, '1124)
 *
 * If the date string is 4 digits (e.g., '1124'), it interprets it as `dmyy`.
 *
 * @example
 * parseDateLenient('1.9.2024');
 * returns Date object for Septembre 1, 2024
 *
 * @example
 * parseDateLenient('01092024');
 * returns Date object for Septembre 1, 2024
 *
 * @example
 * parseDateLenient('1124');
 * returns Date object for January 1, 2024
 */
export const parseDateLenient = (value: DateFieldValue): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  let result = undefined;

  for (const f of ["iso", dateFormat, dateFormatWithWeekday, "d.M.yyyy", "d.M.yy", "ddMMyyyy"]) {
    const locale = getLocale();
    result = f === "iso" ? parseISO(value) : dateFnsParse(value, f, new Date(), { locale });
    if (!isValidDate(result)) {
      continue;
    }

    logger.trace(
      "parseDateLenient(): ",
      value,
      `success with format '${f}' (${locale.code})  ->`,
      dateFnsFormat(result, "yyyy-mm-dd hh:MM:ss")
    );
    return result;
  }

  if (!isValidDate(result) && value.length === 4) {
    const d = parseInt(value.substring(0, 1));
    const m = parseInt(value.substring(1, 2));
    const y = parseInt(`20${value.substring(2)}`); // if 20 is not added, date is constructed as 1922.
    result = new Date(y, m - 1, d);

    if (isValidDate(result)) {
      logger.trace(
        "parseDateLenient(): ",
        value,
        "sucess with short form",
        y,
        m,
        d,
        dateFnsFormat(result, "yyyy-mm-dd HH:MM:ss")
      );
      return result;
    }
  }

  logger.warn("parseDateLenient(): ", value, "could not be parsed -> undefined");
  return undefined;
};

export const isDateNewerThanYesterday = (value: DateFieldValue): boolean => {
  const inputDate = parseDateLenient(value);
  if (!inputDate) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate yesterday's date
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isNewer = inputDate > yesterday;
  logger.trace(`isDateNewerThanYesterday(): ${inputDate} -> ${isNewer}`);

  return isNewer;
};

export const formatDateRelative = (value: DateFieldValue, baseDate = new Date()) => {
  const date = parseDateLenient(value);
  if (!isValidDate(date)) {
    return "";
  }

  const result = dateFnsFormatRelative(date!, baseDate, { weekStartsOn: 1 });
  logger.trace(`formatDateRelative(): ${value} -> ${result}`);

  return result;
};

export function isValidDate(date: Date | undefined | null): boolean {
  if (!date) return false;
  if (!dateFnsIsValid(date)) return false;
  if (getYear(date) < minYear) return false;
  return true;
}

function getLocale() {
  const lang = `${document.documentElement.lang}`.toLowerCase();
  let result = de;
  switch (lang) {
    case "fr":
      result = fr;
      break;
    case "it":
      result = it;
      break;
  }
  logger.trace(`getLocale(): `, lang, result);
  return result;
}

/**
 * Calculates the deletion date for a run based on its archived date.
 * GDPR: Runs are deleted 2 years after being archived.
 * @param archivedDate The date when the run was archived
 * @returns The date when the run will be deleted, or undefined if archivedDate is not provided
 */
export function getDeletionDate(archivedDate: string | Date | undefined | null): Date | undefined {
  if (!archivedDate) return undefined;

  const archived = typeof archivedDate === "string" ? new Date(archivedDate) : archivedDate;
  if (!isValidDate(archived)) return undefined;

  // Add 2 years (730 days to account for leap years)
  return new Date(archived.getTime() + 2 * 365 * 24 * 60 * 60 * 1000);
}

export const toJsDate = (value: Date | string | undefined | null): Date | undefined => {
  let result = undefined;
  if (value) {
    if (typeof value === "string") {
      result = new Date(value);
    } else if (value instanceof Date) {
      result = value as Date;
    }
  }

  logger.trace(`toJsDate(): ${value} (${typeof value}) -> ${result}`);
  return result;
};

export const toServerDate = (value: Date | string | undefined | null): string | undefined => {
  let result = undefined;
  if (value) {
    if (typeof value === "string") {
      result = new Date(value).toISOString();
    } else if (value instanceof Date) {
      result = value.toISOString();
    }
  }

  result = result == null ? undefined : result;
  logger.trace(`toServerDate(): ${value} (${typeof value}) -> ${result}`);
  return result;
};
